// src/api/AppTrackerAPI.js

// Base URL for the backend API. Reads from env var or falls back to the production host.
const BASE_URL = "https://goroutes.dealerclick.com";

// API key injected via environment variables for security.
// Make sure you have REACT_APP_API_KEY set in your .env (and never commit that file).
const API_KEY = "aardvark5000";

// Warn early if the key is missing
if (!API_KEY) {
	// eslint-disable-next-line no-console
	console.warn(
		"[AppTrackerAPI] Missing REACT_APP_API_KEY — requests will likely fail"
	);
}

/**
 * Submits a full loan application to the backend API.
 *
 * @param {Object} payload - The complete application payload (matching `params.json` structure).
 * @param {Object} options - Additional options for the request.
 * @param {string} options.dealerId - Dealer ID to send as a query param.
 * @param {string} options.lenderId - Lender ID to send as a query param.
 * @param {string} options.dealId - Deal ID to send as a query param.
 * @returns {Promise<Object>} The JSON response from the backend API.
 * @throws {Error} If the request fails or returns a non-2xx status.
 */
export async function submitLoanApplication(
	payload,
	{ dealerId, lenderId, dealId }
) {
	const url = new URL("/loan-application", BASE_URL);
	url.searchParams.set("dealer_id", dealerId);
	url.searchParams.set("lender_id", lenderId);
	url.searchParams.set("deal_id", dealId);

	try {
		const response = await fetch(url.toString(), {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"x-api-key": API_KEY,
			},
			body: JSON.stringify(payload), // ✅ send raw payload directly
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(
				`POST ${url.toString()} failed: ${response.status} ${
					response.statusText
				} — ${errorText}`
			);
		}

		return await response.json();
	} catch (err) {
		console.error("[AppTrackerAPI] Error in submitLoanApplication:", err);
		throw err;
	}
}
/**
 * Constructs the full URL for fetching submitted applications.
 *
 * Using the URL constructor avoids accidental double-slashes
 * and makes query-param handling safer.
 *
 * @param {string} dealerId
 * @returns {string}
 */
function buildSubmittedAppsUrl(dealerId) {
	const url = new URL("/submitted-apps", BASE_URL);
	url.searchParams.append("dealer_id", dealerId);
	return url.toString();
}

/**
 * Fetches all submitted applications for a given dealer.
 *
 * @param {string} dealerId - Unique identifier for the dealer.
 * @returns {Promise<Object[]>} Resolves with an array of SubmittedApp objects.
 * @throws {Error} If the network request fails or the server returns a non-2xx status.
 */
export async function getSubmittedAppsByDealer(dealerId) {
	const url = buildSubmittedAppsUrl(dealerId);

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"x-api-key": API_KEY,
			},
		});

		// If the response isn't in the 200–299 range, throw with the status and body.
		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(
				`GET ${url} failed: ${response.status} ${response.statusText} — ${errorText}`
			);
		}

		// Parse and return the JSON payload.
		return await response.json();
	} catch (err) {
		// Log the error for debugging/monitoring; rethrow so callers can handle UI errors.
		// You could integrate Sentry or another monitoring tool here.
		// eslint-disable-next-line no-console
		console.error("[AppTrackerAPI] Error in getSubmittedAppsByDealer:", err);
		throw err;
	}
}

/*
 this is the shape of the data that we need to send to the backend: 
{
    "params": {
        "json": {
            "Comp": "na",
            "DealerId": "87878787",
            "Source": "ds",
            "Type": "joint",
            "AppDate": "2021-09-01 21:34:21",
            "PaymentCall": "N",
            "Borrower": {
                "Ssn": "777335689",
                "Type": "borrower",
                "FirstName": "Andy",
                "LastName": "Test2",
                "MiddleName": "A",
                "Dob": "1975-03-09",
                "IsUsCitizen": "Y",
                "HomePhone": "5555551234"
            },
            "BorrowerAddressLoanReq": [
                {
                    "Type": "homeaddress",
                    "Status": "current",
                    "Address": "7820 Baymeadows Rd E",
                    "City": "Jacksonville",
                    "State": "FL",
                    "ZipCode": "32256",
                    "TimeAtResYears": 2,
                    "TimeAtResMonths": 2,
                    "OwnershipType": "rent",
                    "MonthlyPmt": 1200
                }
            ],
            "BorrowerEmployments": [
                    {
                        "CurrentEmploymnt": "Y",
                        "Status": "full_time",
                        "Name": "Test Company",
                        "ContactPhone": "5555551234",
                        "JobPos": "Tester",
                        "TimeEmpYears": 3,
                        "TimeEmpMonths": 0,
                        "GrossMonthlyInc": 5469.15,
                        "Income": 2525,
                        "Period": "bi_weekly"
                    }
                ],
            "CoBorrower": {
                "Ssn": "777114477",
                "Type": "coborrower",
                "FirstName": "Sally",
                "LastName": "Tester2",
                "Dob": "1975-03-03",
                "Age": 45,
                "IsUsCitizen": "Y",
                "HomePhone": "5558885689"
            },
            "CoBorrowerAddressLoanReq": [
                {
                    "Type": "homeaddress",
                    "Status": "current",
                    "Address": "7820 Baymeadows Rd E",
                    "City": "Jacksonville",
                    "State": "FL",
                    "ZipCode": "32256",
                    "TimeAtResYears": 2,
                    "TimeAtResMonths": 0,
                    "OwnershipType": "rent",
                    "MonthlyPmt": 0
                }
            ],
            "CoBorrowerEmployments": [
                {
                    "CurrentEmploymnt": "Y",
                    "Status": "part_time",
                    "Name": "mcdonalds",
                    "ContactPhone": "9045551234",
                    "JobPos": "cook",
                    "TimeEmpYears": 1,
                    "TimeEmpMonths": 1,
                    "GrossMonthlyInc": 2166,
                    "Income": 1000,
                    "Period": "bi_weekly"
                }
            ],
            "LoanRequest": {
                "FinTypeCode": "auto_installment_indirect",
                "Term": 48,
                "NbrPayments": 48,
                "TotalCashPrice": 15750,
                "DownPmt": 2500,
                "DocFee": 150,
                "LicenseTagsReg": 250,
                "SalesTax": 500,
                "AmtToFin": 14150,
                "ReqPmt": 329.08,
                "PmtFreq": "monthly",
                "FicoScore": 650,
                "TotalDebt": 0,
                "TradeInAllow": 0,
                "ServContractCode": "none",
                "ServContractAmt": 0,
                "InterestRate": 5.5,
                "GapCode": "none",
                "InsuranceTerm": 0,
                "InsuranceAmt": 0,
                "GapInsAmt": 0,
                "OtherTaxable": 0,
                "OtherNonTaxable": 0,
                "PayoffOnTrade": 0,
                "Rebate": 0
            },
            "LoanRequestVehicle": {
                "State": "CO",
                "EvalType": "NADA",
                "ZipCode": 32207,
                "Vin": "XYUGRFT563893GFDT",
                "VehicleYear": 2015,
                "EvalDate": "2018-12-25",
                "VehicleTrim": "LT 2dr Regular Cab SB (4.3L 6cyl 6A)",
                "Make": "Chevrolet",
                "Mileage": 7500,
                "Model": "Silverado 1500",
                "Bookflag": "U",
                "VehicleClass": "UsedCar",
                "Mode": "external",
                "Manual": "Y",
                "BaseValue1": 13275,
                "MileageAdjValue1": 0,
                "OptionsValue1": 0,
                "TotalValue1": 13275,
                "ValueType1": "TradeIn",
                "BaseValue2": 0,
                "MileageAdjValue2": 0,
                "OptionsValue2": 0,
                "TotalValue2": 0
            }
        }
    },
    "action": "SaveSubmit",
    "callback_url": "https://goroutes.dealerclick.com/loan-application/status"
}
*/
